<template>
  <b-form @keydown="form.errors.clear($event.target.name)">
    <b-button-group class="mb-3">
      <b-button :variant="type === 'all' ? 'success' : ''" @click.prevent="type = 'all'"
        >All Appointments
      </b-button>
      <b-button :variant="type === 'past' ? 'warning' : ''" @click.prevent="type = 'past'"
        >Past
      </b-button>
      <b-button :variant="type === 'future' ? 'info' : ''" @click.prevent="type = 'future'"
        >Future
      </b-button>
    </b-button-group>

    <!-- Not using b-list-group because it seems to break v-infinite-scroll -->
    <ul
      v-show="appointments && appointments.length > 0"
      v-infinite-scroll="loadMore"
      class="list-group overflow-y-auto mb-3"
      infinite-scroll-disabled="loadingMore"
      infinite-scroll-distance="30"
      style="max-height: 309px"
    >
      <!-- eslint-disable -->
      <li
        v-for="appointment in appointments"
        v-if="shouldShow(appointment)"
        :key="appointment.id"
        :class="[
          appointment.id === editingAppointmentId ? 'active' : '',
          isPast(appointment.time) ? 'past' : '',
          bulkDeleteAppointmentIds.includes(appointment.id) ? 'alt-selected' : '',
        ]"
        class="list-group-item highlight cursor"
        @click.left.exact="editAppointment(appointment)"
        @click.alt.left.exact.prevent="addBulkDelete(appointment)"
      >
        <div v-if="appointment.appointment_number" class="d-inline-block mr-1">
          <appointment-object :appointmentNumber="appointment.appointment_number" />
        </div>

        <b-alert class="text-12 font-weight-bold d-inline-block" show variant="warning">
          <font-awesome-icon :icon="['fal', 'clock']" />
          {{ formatDate(appointment.time) }}
          for
          {{ appointment.duration }}
          {{ parseInt(appointment.duration) > 1 ? " hours" : " hour" }}
        </b-alert>

        <div class="w-100">
          <user-object
            :entity-id="appointment.user.id"
            :name="appointment.user.name"
            :open-new="true"
            class="mr-1"
          />
        </div>

        <div v-if="appointment.assistants.length > 0" class="w-100 pr-3 pt-1">
          <span class="text-muted text-13 mr-1">Assistants:</span>
          <user-object
            v-for="assistant in appointment.assistants"
            :key="`assistant-${assistant.id}`"
            :entity-id="assistant.id"
            :icon="['fal', 'user']"
            :name="assistant.name"
            :open-new="true"
            class="mr-1"
            color="opacity-75 badge-pill badge-secondary"
          />
        </div>

        <div class="w-100 mt-1">
          <div v-if="appointment.scrutinised" class="badge badge-primary badge-large mr-1">
            <font-awesome-icon :icon="['fal', 'thumbs-up']" />
          </div>

          <div class="badge badge-danger badge-large mr-1">
            {{ appointment.state }}
          </div>

          <div v-if="appointment.notes && appointment.notes.length > 0" class="d-inline-block mr-1">
            <notes-object :tooltip="appointment.notes" />
          </div>

          <div class="badge badge-info badge-large">{{ appointment.type }}</div>
          <div
            class="badge badge-large"
            :class="appointment.swms ? 'badge-success' : 'badge-danger'"
          >
            {{ appointment.swms ? "SWMS" : "NO SWMS" }}
          </div>
          <div
            class="badge badge-large"
            :class="appointment.roof_report ? 'badge-success' : 'badge-danger'"
          >
            {{ appointment.roof_report ? "Roof Report" : "NO Roof Report" }}
          </div>

          <div v-if="appointment.recurring" class="badge badge-warning badge-large">Recurring</div>
        </div>

        <font-awesome-icon
          v-if="!isPast(appointment.time)"
          :icon="['fal', 'trash']"
          class="trash-entity"
          @click.stop="deleteAppointment(appointment)"
        />
      </li>
    </ul>

    <!-- Fields -->
    <div v-show="bulkDeleteAppointmentIds.length === 0">
      <b-alert show variant="info" class="mb-2 alert-sm" v-if="type !== 'past'">
        <code>LEFT ALT + Click</code> to select multiple appointments to delete
      </b-alert>

      <div v-if="$store.state.jobs.currentJob.job_address_id !== null">
        <form-bootstrap-radio
          v-if="creating && $can('create', 'appointments', 'recurring')"
          v-model="form.recurring.type"
          :options="recurringTypes"
          :single-row="false"
          name="recurring_type"
          @input="recurringTypeChange()"
        />

        <div class="row">
          <div v-show="form.recurring.type === 'Not Recurring'" class="col">
            <form-date
              v-model="formDate"
              :single-row="false"
              error-name="time"
              name="date"
              @input="formDateChanged"
            />
          </div>
          <div class="col">
            <form-time v-model="formTime" :single-row="false" name="time" start="06:00"
              >Time (24h)</form-time
            >
          </div>
          <div class="col">
            <form-select
              v-model="form.duration"
              :options="durations"
              :single-row="false"
              name="duration"
              @input="form.errors.clear()"
            />
          </div>
        </div>
        <template v-if="form.recurring.type !== 'Not Recurring'">
          <!-- Weekly -->
          <div v-if="form.recurring.type === 'Weekly'" class="row">
            <div class="col">
              <form-select
                v-model="form.recurring.weekly_interval"
                :options="calendar.weeklyIntervals"
                :single-row="false"
                error-name="recurring.weekly_interval"
                name="weekly_interval"
                @input="form.errors.clear()"
              />
            </div>
            <div class="col">
              <div class="form-row">
                <label class="col-12 mb-2"> On Days of Week </label>
                <div
                  :class="{ 'invalid-wrapper': form.errors.has('recurring.weekly_days') }"
                  class="col-12 mb-3"
                >
                  <multiselect
                    v-model="form.recurring.weekly_days"
                    :clear-on-select="false"
                    :close-on-select="false"
                    :hide-selected="true"
                    :multiple="true"
                    :options="calendar.weeklyDays"
                    :preserve-search="true"
                    label="name"
                    placeholder="Select days"
                    track-by="name"
                    @input="form.errors.clear('recurring.weekly_days')"
                  />
                  <div
                    class="invalid-feedback"
                    v-text="form.errors.get('recurring.weekly_days')"
                  ></div>
                </div>
              </div>
            </div>
          </div>

          <div v-if="form.recurring.type === 'Monthly'" class="row">
            <div class="col">
              <form-select
                v-model="form.recurring.monthly_ordinal"
                :options="calendar.weekDayOrdinals"
                :single-row="false"
                error-name="recurring.monthly_ordinal"
                name="monthly_ordinal"
                @input="form.errors.clear()"
                >The
              </form-select>
            </div>
            <div class="col">
              <form-select
                v-model="form.recurring.monthly_day"
                :options="calendar.days"
                :single-row="false"
                name="monthly_day"
                @input="form.errors.clear()"
                >Day
              </form-select>
            </div>
            <div class="col">
              <form-select
                v-model="form.recurring.monthly_interval"
                :options="calendar.monthlyIntervals"
                :single-row="false"
                error-name="recurring.monthly_interval"
                name="monthly_interval"
                @input="form.errors.clear()"
                >Of every
              </form-select>
            </div>
          </div>

          <div v-if="form.recurring.type === 'Yearly'" class="row">
            <div class="col">
              <form-select
                v-model="form.recurring.yearly_ordinal"
                :options="calendar.weekDayOrdinals"
                :single-row="false"
                error-name="recurring.yearly_ordinal"
                name="yearly_ordinal"
                @input="form.errors.clear()"
                >The
              </form-select>
            </div>
            <div class="col">
              <form-select
                v-model="form.recurring.yearly_day"
                :options="calendar.days"
                :single-row="false"
                error-name="recurring.yearly_day"
                name="yearly_day"
                @input="form.errors.clear()"
                >Day
              </form-select>
            </div>
            <div class="col">
              <form-select
                v-model="form.recurring.yearly_month"
                :options="calendar.months"
                :single-row="false"
                error-name="recurring.yearly_month"
                name="yearly_month"
                @input="form.errors.clear()"
                >Of
              </form-select>
            </div>
          </div>

          <div class="row">
            <div class="col">
              <form-date
                v-model="form.recurring.create_from"
                :pickerType="rangePickerType"
                :single-row="false"
                error-name="recurring.create_from"
                name="create_from"
              >
                Create from
              </form-date>
            </div>
            <div class="col">
              <form-text
                v-model.trim="form.recurring.occurences"
                :is-number="true"
                :single-row="false"
                error-name="recurring.occurences"
                max="24"
                min="2"
                name="occurences"
                @input="clearEndErrors"
                >End after (times)
              </form-text>
            </div>
            <div class="col">
              <form-date
                v-model="form.recurring.not_later_than"
                :pickerType="rangePickerType"
                :single-row="false"
                error-name="recurring.not_later_than"
                name="not_later_than"
                @input="clearEndErrors"
              >
                Not later than
              </form-date>
            </div>
          </div>
        </template>
        <div class="row">
          <div class="col">
            <form-bootstrap-radio
              v-model="form.type"
              :single-row="false"
              :options="['Site Visit', 'Phone Consult']"
              name="type"
            />
            <form-user-select
              v-if="$can('update', 'appointments', 'assign-others')"
              v-model="form.user"
              :filter-by-brand-id="$store.state.jobs.currentJob.brand_id"
              :has-customer="$store.state.jobs.currentJob.customer_id"
              :single-row="false"
              name="user_id"
              placeholder="Search technician..."
              @change="updateAppointed"
            >
              Technician
            </form-user-select>
            <form-text
              v-else-if="form.user"
              v-model="form.user.name"
              :readonly="true"
              :single-row="false"
              name="user_id"
            />
            <form-text
              v-else
              v-model="nullUser"
              :readonly="true"
              :single-row="false"
              name="user_id"
            />
          </div>
        </div>
        <div v-if="$can('update', 'appointments', 'assistants')" class="row">
          <div class="col">
            <form-user-select
              v-model="form.assistants"
              :filter-by-brand-id="$store.state.jobs.currentJob.brand_id"
              :multiple="true"
              :single-row="false"
              name="assistants"
              placeholder="Search assistants..."
              @change="updateAssistants"
            >
              Assistants
            </form-user-select>
          </div>
        </div>
        <template v-if="!creating">
          <div v-if="$can('read', 'appointments', 'states')" class="row">
            <div class="col">
              <form-select
                v-model="form.state"
                :options="states"
                :single-row="false"
                name="state"
                @input="form.errors.clear('state')"
              >
                Appointment State
              </form-select>
            </div>
          </div>

          <check-ins
            v-if="editingAppointmentId && $can('read', 'appointments', 'check-ins')"
            :appointmentId="editingAppointmentId"
            :key="editingAppointmentId"
          />

          <div v-if="$can('read', 'appointments', 'compliances')" class="row card-footer pb-3 mb-2">
            <div class="col">
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Note Added</label>
                <core-switch
                  v-model="form.compliance.note_added"
                  class="float-right mr-2"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Invoice Created</label>
                <core-switch
                  v-model="form.compliance.invoice_created"
                  class="float-right mr-2"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Invoice Paid</label>
                <core-switch
                  v-model="form.compliance.invoice_paid"
                  class="float-right mr-2"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Blog Worthy</label>
                <core-switch
                  v-model="form.compliance.blog_worthy"
                  class="float-right mr-2"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
            </div>
            <div class="col">
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Picture Added</label>
                <core-switch
                  v-model="form.compliance.picture_added"
                  class="float-right"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Signature</label>
                <core-switch
                  v-model="form.compliance.signature_added"
                  class="float-right"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
              <div class="switch-row">
                <label class="col-12 d-inline pl-0">Authorised</label>
                <core-switch
                  v-model="form.compliance.authorised"
                  class="float-right"
                  color="primary"
                  off="N"
                  on="Y"
                  variant="3d"
                >
                </core-switch>
              </div>
            </div>
          </div>

          <div v-if="$can('read', 'appointments', 'scrutinies')" class="mb-3">
            <div class="form-group row w-100">
              <div class="col-form-label pl-2">
                <label>Scrutiny Completed</label>
              </div>
              <div>
                <core-switch
                  v-model="form.scrutinised"
                  :dataOff="'\u2715'"
                  :dataOn="'\u2713'"
                  class="d-inline-block ml-2"
                  color="primary"
                  label
                  variant="pill"
                />
              </div>
            </div>
          </div>
        </template>
        <div class="row">
          <div class="col">
            <form-textarea v-model="form.notes" :single-row="false" name="notes" rows="2" />
          </div>
        </div>
      </div>
      <div v-else>
        <div class="text-muted">
          Cannot create appointment until the job has a valid job address.
        </div>
      </div>

      <div :class="{ 'invalid-wrapper': form.errors.has('job') }">
        <p
          v-if="form.errors.has('job')"
          class="invalid-feedback full-size"
          v-text="form.errors.get('job')"
        ></p>
      </div>

      <div v-if="$store.state.jobs.currentJob.job_address_id !== null" class="clearfix">
        <button
          v-tooltip="{
            content: invalid,
            classes: ['badge-theme'],
          }"
          :class="{
            disabled: form.notReady(),
            'btn-primary': !invalid,
            'btn-warning': invalid,
          }"
          class="btn float-right"
          type="button"
          @click.prevent="onSubmit"
        >
          <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
          <font-awesome-icon v-if="!form.busy && creating" :icon="['fal', 'plus-circle']" />
          <font-awesome-icon v-if="!form.busy && !creating" :icon="['fal', 'pencil']" />
          {{ creating ? "Add" : "Save" }}
        </button>

        <button v-if="!creating" class="btn btn-secondary" @click.prevent="newAppointment">
          <font-awesome-icon :icon="['fal', 'undo']" />
          New Appointment
        </button>
      </div>
    </div>

    <div class="row" v-if="bulkDeleteAppointmentIds.length > 0">
      <div class="col">
        <button
          :disabled="form.notReady()"
          class="btn btn-secondary"
          @click.prevent="resetbulkDelete()"
        >
          <font-awesome-icon :icon="['fal', 'undo']" />
          Reset
        </button>
      </div>
      <div class="col text-right">
        <button :disabled="form.notReady()" class="btn btn-danger" @click.prevent="deleteBatch">
          <font-awesome-icon v-if="form.busy" :icon="['fal', 'spinner']" spin />
          <font-awesome-icon v-else :icon="['fal', 'trash']" />
          Delete selected
        </button>
      </div>
    </div>
  </b-form>
</template>

<script type="text/babel">
import moment from "moment";

import CheckIns from "./Appointment/CheckIns.vue";

import calendar from "../../../mixins/calendar";
import format from "../../../mixins/format";
import EventBus from "../../../imports/event-bus";

const recurringTypes = ["Not Recurring", "Weekly", "Monthly", "Yearly"];

const durations = [
  { id: "0.5", name: "0.5 Hour" },
  { id: "1.0", name: "1 Hour" },
  { id: "1.5", name: "1.5 Hour" },
  { id: "2.0", name: "2 Hours" },
  { id: "2.5", name: "2.5 Hours" },
  { id: "3.0", name: "3 Hours" },
  { id: "3.5", name: "3.5 Hours" },
  { id: "4.0", name: "4 Hours" },
  { id: "4.5", name: "4.5 Hours" },
  { id: "5.0", name: "5 Hours" },
  { id: "5.5", name: "5.5 Hours" },
  { id: "6.0", name: "6 Hours" },
  { id: "6.5", name: "6.5 Hours" },
  { id: "7.0", name: "7 Hours" },
  { id: "7.5", name: "7.5 Hours" },
  { id: "8.0", name: "8 Hours" },
];

const states = [
  "Ready",
  "Confirmed",
  "Travelling",
  "In Progress",
  "Overdue",
  "Attended",
  "Not Attended",
];

const initialState = {
  id: "",
  type: "Site Visit",
  user: "",
  user_id: "",
  job_id: "",
  time: "",
  duration: "1.0",
  notes: "",
  state: "Ready",
  scrutinised: false,
  assistants: [],

  recurring: {
    type: "Not Recurring",
    weekly_interval: 1,
    weekly_days: [],

    monthly_ordinal: "First",
    monthly_day: "Monday",
    monthly_interval: 1,

    yearly_ordinal: "First",
    yearly_day: "Monday",
    yearly_month: "January",

    create_from: "",
    occurences: "",
    not_later_than: "",
  },

  compliance: {
    note_added: false,
    picture_added: false,
    signature_added: false,
    invoice_created: false,
    invoice_paid: false,
    authorised: false,
    blog_worthy: false,
  },
};

export default {
  components: {
    CheckIns,
  },

  mixins: [calendar, format],

  props: {
    currentJobId: {
      type: Number,
      required: true,
    },

    job: {
      required: true,
    },
  },

  data() {
    return {
      states,
      durations,
      recurringTypes,

      type: "future",
      initialised: false,
      creating: true,
      appointments: [],
      editingAppointmentId: null,
      bulkDeleteAppointmentIds: [],
      currentAvailableUsers: [],

      total: null,
      page: null,
      lastPage: null,
      loadingMore: false,

      form: new window.AppForm(window.clone(initialState)),
      formDate: "",
      formTime: "",
      techBooking: null,

      rangePickerType: "date",
      nullUser: null,
    };
  },

  computed: {
    invalid() {
      const jobContact = this.$lodash.get(this.$store.state.jobs.currentJob, "job_contacts[0]");

      if (!jobContact) {
        return "The current job does not have a valid job contact";
      }

      if (!this.isValidEmail(jobContact.email)) {
        return "The current job contact has an invalid email";
      }

      if (!this.isValidMobile(jobContact.mobile)) {
        return "The current job contact has an invalid mobile";
      }

      if (this.form.notReady()) {
        return "The form is not ready";
      }

      return false;
    },
  },

  watch: {
    currentJobId() {
      this.init();
    },
  },

  mounted() {
    this.init();

    EventBus.$on("job-updated", () => {
      this.form.errors.clear();
    });

    EventBus.$on("service-type-changed", (serviceTypes) => {
      this.newAppointment();
      this.initDefaultAppointmentDuration(serviceTypes);
    });

    EventBus.$on("availability-current-users-updated", (users) => {
      this.currentAvailableUsers = users;
    });
  },

  beforeUnmount() {
    EventBus.$off("availability-current-users-updated");
  },

  methods: {
    init() {
      this.form.startProcessing();

      this.formDate = "";
      this.formTime = "";
      this.form.reset();
      this.form.job_id = this.currentJobId;
      this.form.recurring = window.clone(initialState.recurring);
      this.form.recurring.create_from = new Date();

      this.type = "future";
      this.creating = true;
      this.editingAppointmentId = null;

      // Init duration to longest duration provided by chosen service types in Job Edit tab
      this.initDefaultAppointmentDuration();

      this.maybeSetFormUserToAuthUser();

      // noinspection JSUnresolvedFunction
      return this.$http
        .get(`/jobs/${this.currentJobId}/appointments?limit=200`)
        .then((response) => {
          this.appointments = window.clone(response.body.data);
          this.total = response.body.total;
          this.page = response.body.current_page;
          this.lastPage = response.body.last_page;

          this.form.finishProcessing();
          this.initialised = true;

          this.getTechBooking();

          return new Promise((resolve) => {
            resolve(response);
          });
        })
        .catch(() => {
          this.form.finishProcessing();
        });
    },

    updateStoreRecord(response) {
      this.$store.commit("jobs/updateRecord", {
        id: this.currentJobId,
        appointments: window.clone(response.body.data),
      });

      this.$store.dispatch("tech-availability/reload");
    },

    onSubmit() {
      if (this.form.notReady()) {
        return false;
      }

      if (this.creating) {
        if (this.form.recurring.type === "Not Recurring") {
          return this.createSingleAppointment();
        }

        return this.createRecurringAppointments();
      }

      return this.updateAppointment();
    },

    createRecurringAppointments() {
      // Parse this.form.time to format backend accept
      if (!this.formTime || this.formTime.length === 0) {
        this.form.time = "";
      } else {
        this.form.time = `${this.formTime}:00`;
      }

      // Determine create from and not later than based on recurring type
      const createFromMoment = moment(this.form.recurring.create_from);
      const notLaterThanMoment = moment(this.form.recurring.not_later_than);
      switch (this.form.recurring.type) {
        case "Monthly":
          this.form.recurring.create_from = createFromMoment.startOf("month").format("YYYY-MM-DD");
          this.form.recurring.not_later_than = notLaterThanMoment
            .endOf("month")
            .format("YYYY-MM-DD");
          break;

        case "Yearly":
          this.form.recurring.create_from = createFromMoment.startOf("year").format("YYYY-MM-DD");
          this.form.recurring.not_later_than = notLaterThanMoment
            .endOf("year")
            .format("YYYY-MM-DD");
          break;

        default:
          this.form.recurring.create_from = createFromMoment.format("YYYY-MM-DD");
          this.form.recurring.not_later_than = notLaterThanMoment.format("YYYY-MM-DD");
      }

      this.form.recurring.create_from =
        this.form.recurring.create_from === "Invalid date" ? "" : this.form.recurring.create_from;
      this.form.recurring.not_later_than =
        this.form.recurring.not_later_than === "Invalid date"
          ? ""
          : this.form.recurring.not_later_than;

      this.form.post("/appointments").then((response) => {
        this.init();

        if (response) {
          if (response.length > 0) {
            const appointmentWord = response.length === 1 ? "appointment" : "appointments";

            window.toastr.success(
              `${response.length} recurring ${appointmentWord} added!`,
              "Success"
            );

            EventBus.$emit("appointment-created", response[0]);

            // Tell jobs store that this job is now recurring job
            this.$store.commit("jobs/updateRecord", {
              id: this.currentJobId,
              is_recurring: response[0].id,
            });
          } else {
            window.toastr.warning(
              "No appointment created based on recurring definition!",
              "Warning"
            );
          }
        } else {
          window.toastr.error(
            "Something went wrong when trying to create recurring appointments!",
            "Error"
          );
        }
      });
    },

    createSingleAppointment() {
      // Parse this.form.time to format backend accept
      if (
        !this.formDate ||
        this.formDate.length === 0 ||
        !this.formTime ||
        this.formTime.length === 0
      ) {
        this.form.time = "";
      } else {
        this.form.time = `${moment(this.formDate).format("YYYY-MM-DD")} ${this.formTime}:00`;
      }

      this.form.post("/appointments").then((response) => {
        this.init().then(this.updateStoreRecord);
        EventBus.$emit("appointment-created", response);
        window.toastr.success("Appointment added!", "Success");

        // Prompt if to set a Completed Job to Warranty status
        if (this.$store.state.jobs.currentJob.status === "Completed") {
          window
            .sweetAlert({
              title: "Warranty job?",
              text: 'Do you want to set this job to "Warranty"?',
              icon: "warning",
              dangerMode: true,
              buttons: {
                cancel: "No",
                confirm: "Yes!",
              },
            })
            .then((confirmed) => {
              if (confirmed) {
                const jobUpdateForm = new window.AppForm({
                  status: "Warranty",
                });
                jobUpdateForm
                  .put(`/jobs/${this.currentJobId}?with=jobAddress;jobContacts;serviceTypes`)
                  .then((jobResponse) => {
                    if (jobResponse) {
                      this.$store.commit("jobs/updateRecord", jobResponse);
                      this.$store.commit("jobs/setCurrentJob", jobResponse);
                      this.$store.dispatch("tech-availability/reload");
                      EventBus.$emit("to-refresh-job");
                      EventBus.$emit("job-updated");
                    } else {
                      window.toastr.error("Failed to update the job to Warranty status", "Error");
                      // noinspection JSUnresolvedFunction
                      this.$root.reload();
                    }
                  });
              }
            });

          return;
        }

        // Prompt if to set a Quoted Job (with accepted quotes) to Work Order status
        if (this.$store.state.jobs.currentJob.status === "Quoted") {
          this.$http.get(`/jobs/${this.currentJobId}/invoices?quote=1&accepted=1`).then((found) => {
            if (this.$lodash.get(found, "body.total") > 0) {
              window
                .sweetAlert({
                  title: "Work Order job?",
                  text: 'Do you want to set this job to "Work Order"?',
                  icon: "warning",
                  dangerMode: true,
                  buttons: {
                    cancel: "No",
                    confirm: "Yes!",
                  },
                })
                .then((confirmed) => {
                  if (confirmed) {
                    const jobUpdateForm = new window.AppForm({
                      status: "Work Order",
                    });
                    jobUpdateForm
                      .put(`/jobs/${this.currentJobId}?with=jobAddress;jobContacts;serviceTypes`)
                      .then((jobResponse) => {
                        if (jobResponse) {
                          this.$store.commit("jobs/updateRecord", jobResponse);
                          this.$store.commit("jobs/setCurrentJob", jobResponse);
                          this.$store.dispatch("tech-availability/reload");
                          EventBus.$emit("to-refresh-job");
                          EventBus.$emit("job-updated");
                        } else {
                          window.toastr.error(
                            "Failed to update the job to Work Order status",
                            "Error"
                          );
                          // noinspection JSUnresolvedFunction
                          this.$root.reload();
                        }
                      });
                  }
                });
            }
          });
        }

        // Prompt if to set a Non-Recurring Job to Recurring when appointments already exist
        if (this.$store.state.jobs.currentJob.is_recurring === 0 && this.appointments.length > 0) {
          window
            .sweetAlert({
              title: "Recurring job?",
              text: 'Do you want to set this job to "Recurring"?',
              icon: "warning",
              dangerMode: true,
              buttons: {
                cancel: "No",
                confirm: "Yes!",
              },
            })
            .then((confirmed) => {
              if (confirmed) {
                const jobUpdateForm = new window.AppForm({
                  is_recurring: 1,
                });
                jobUpdateForm.put(`/jobs/${this.currentJobId}`).then((jobResponse) => {
                  if (jobResponse) {
                    this.$store.commit("jobs/updateRecord", jobResponse);
                    this.$store.commit("jobs/setCurrentJob", jobResponse);
                    this.$store.dispatch("tech-availability/reload");
                    EventBus.$emit("to-refresh-job");
                    EventBus.$emit("job-updated");
                  } else {
                    window.toastr.error("Failed to update the job to Recurring", "Error");
                    // noinspection JSUnresolvedFunction
                    this.$root.reload();
                  }
                });
              }
            });
        }
      });
    },

    updateAppointment() {
      // Parse this.form.time to format backend accept
      if (
        !this.formDate ||
        this.formDate.length === 0 ||
        !this.formTime ||
        this.formTime.length === 0
      ) {
        this.form.time = "";
      } else {
        this.form.time = `${moment(this.formDate).format("YYYY-MM-DD")} ${this.formTime}:00`;
      }

      this.form.put(`/appointments/${this.form.id}`).then((response) => {
        this.init().then(this.updateStoreRecord);
        EventBus.$emit("appointment-updated", response);
        EventBus.$emit("to-refresh-job");
        EventBus.$emit("job-updated");
        window.toastr.success("Appointment updated!", "Success");
      });
    },

    addBulkDelete(appointment) {
      if (!appointment || !appointment.id || this.isPast(appointment.time)) {
        return false;
      }

      if (this.bulkDeleteAppointmentIds.includes(appointment.id)) {
        this.bulkDeleteAppointmentIds = this.bulkDeleteAppointmentIds.filter(
          (id) => id !== appointment.id
        );
        return;
      }
      this.bulkDeleteAppointmentIds.push(appointment.id);
    },

    deleteBatch() {
      if (this.form.busy) {
        return;
      }

      const count = this.bulkDeleteAppointmentIds.length;
      const entryWord = count > 1 ? "entries" : "entry";
      const word = count > 1 ? "appointments" : "appointment";

      window
        .sweetAlert({
          title: "Are you sure?",
          text: `Are you sure that you want to delete the ${count} selected ${entryWord}?`,
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.form.busy = true;

            this.$http
              .delete("/appointments/batch", { body: { ids: this.bulkDeleteAppointmentIds } })
              .then((response) => {
                if (response.status === 200) {
                  this.init().then(() => {
                    this.bulkDeleteAppointmentIds = [];
                    this.$store.dispatch("tech-availability/reload");
                    EventBus.$emit("appointment-deleted");
                  });

                  window.toastr.success(
                    `The ${count} selected ${word} have been deleted!`,
                    "Success"
                  );
                }

                this.form.busy = false;
              });
          }
        });
    },

    resetbulkDelete() {
      this.bulkDeleteAppointmentIds = [];
    },

    editAppointment(appointment) {
      this.form.id = appointment.id;
      this.form.type = appointment.type;
      this.form.user = appointment.user;
      this.form.user_id = appointment.user_id;
      this.form.duration = appointment.duration;
      this.form.notes = appointment.notes;
      this.form.state = appointment.state;
      this.form.assistants = appointment.assistants;
      this.form.time = "";

      this.form.compliance.note_added =
        this.$lodash.get(appointment, "compliance.note_added", false) === 1;
      this.form.compliance.invoice_created =
        this.$lodash.get(appointment, "compliance.invoice_created", false) === 1;
      this.form.compliance.invoice_paid =
        this.$lodash.get(appointment, "compliance.invoice_paid", false) === 1;
      this.form.compliance.picture_added =
        this.$lodash.get(appointment, "compliance.picture_added", false) === 1;
      this.form.compliance.signature_added =
        this.$lodash.get(appointment, "compliance.signature_added", false) === 1;
      this.form.compliance.authorised =
        this.$lodash.get(appointment, "compliance.authorised", false) === 1;
      this.form.compliance.blog_worthy =
        this.$lodash.get(appointment, "compliance.blog_worthy", false) === 1;

      this.form.scrutinised = this.$lodash.get(appointment, "scrutinised", false) === 1;

      this.formDate = new Date(appointment.time);
      this.formTime = moment(appointment.time, "YYYY-MM-DD HH:mm:ss").format("HH:mm");

      this.form.recurring = window.clone(initialState.recurring);
      this.creating = false;
      this.editingAppointmentId = appointment.id;

      this.formDateChanged();
    },

    deleteAppointment(appointment) {
      window
        .sweetAlert({
          title: "Are you sure?",
          text: "Are you sure that you want to delete this appointment?",
          icon: "warning",
          dangerMode: true,
          buttons: {
            cancel: "No",
            confirm: "Yes!",
          },
        })
        .then((confirmed) => {
          if (confirmed) {
            this.$http.delete(`/appointments/${appointment.id}`).then((response) => {
              // Remove this from local data this.appointments
              const appointmentId = parseInt(response.body.id, 10);
              const appointmentIndex = this.appointments
                .map((thisAppointment) => thisAppointment.id)
                .indexOf(appointmentId);
              this.appointments.splice(appointmentIndex, 1);
              this.total = this.total - 1;

              // Remove from the data in the store
              // this.$store.commit('jobs/removeAppointment', response.body);
              // Omitted now, since we are not storing appointments in jobs data store

              // Reload
              this.$store.dispatch("tech-availability/reload");

              EventBus.$emit("appointment-deleted", response.body);

              this.newAppointment();
            });
          }
        });
    },

    newAppointment() {
      this.form.id = null;
      this.form.type = "Site Visit";
      this.form.user = null;
      this.form.user_id = null;
      this.form.date = null;
      this.form.time = null;
      this.form.duration = 1;
      this.form.assistants = [];
      this.form.notes = "";
      this.form.state = "Ready";
      this.form.scrutinised = false;

      this.creating = true;
      this.editingAppointmentId = null;

      if (this.techBooking) {
        this.techBooking.name = `${this.techBooking.name} [Tech Booking]`;
        this.form.user = this.techBooking;
        this.form.user_id = this.techBooking.id;
      }

      this.maybeSetFormUserToAuthUser();

      this.formDate = null;
      this.formDateChanged();
    },

    maybeSetFormUserToAuthUser() {
      const user = this.$auth.user();

      if (
        user?.designation?.restricted_to_brands ||
        user?.designation?.restricted_to_state_brands
      ) {
        // Do not set user to current user if it is restricted to state brands (aka franchisor)
        return;
      }

      if (user.role === "technician") {
        this.form.user = this.$auth.user();
        this.form.user_id = this.form.user.id;
      }
    },

    getTechBooking() {
      if (this.creating) {
        let customerId = this.$store.getters["jobs/getJobById"](this.currentJobId).customer_id;

        if (!customerId) {
          customerId = this.job.customer_id;
        }

        if (customerId) {
          this.$http.get(`/customers/${customerId}/picked-up-by`).then((response) => {
            if (response.body.id) {
              this.techBooking = response.body;
              this.techBooking.name = `${this.techBooking.name} [Tech Booking]`;
              this.form.user = this.techBooking;
              this.form.user_id = this.techBooking.id;
            } else {
              this.techBooking = null;
              this.form.user = null;
              this.form.user_id = null;

              this.maybeSetFormUserToAuthUser();
            }
          });
        }
      }
    },

    updateAppointed(user) {
      if (!user) {
        return;
      }

      this.form.user = user;
      this.form.user_id = user.id;

      const userExists = this.$lodash.some(this.currentAvailableUsers, { id: user.id });
      if (userExists) {
        this.form.user = user;
        this.form.user_id = user.id;
      } else {
        window
          .sweetAlert({
            title: "Are you sure?",
            text:
              "The selected technician is NOT in the list of those available for this appointment.",
            icon: "info",
            dangerMode: true,
            buttons: {
              cancel: "No",
              confirm: "Yes",
            },
          })
          .then((confirm) => {
            if (confirm) {
              this.form.user = user;
              this.form.user_id = user.id;
            }
          });
      }

      this.form.errors.clear();
    },

    updateAssistants(users) {
      this.form.assistants = users;

      this.form.errors.clear();
    },

    loadMore() {
      if (this.page < this.lastPage) {
        this.loadingMore = true;
        const page = this.page + 1;

        this.$http
          .get(`/jobs/${this.currentJobId}/appointments?page=${page}&limit=200`)
          .then((response) => {
            this.appointments = this.appointments.concat(response.body.data);
            this.page = response.body.current_page;
            this.lastPage = response.body.last_page;

            this.loadingMore = false;
          });
      }
    },

    recurringTypeChange() {
      this.form.time = "";

      switch (this.form.recurring.type) {
        case "Monthly":
          this.rangePickerType = "month";
          break;

        case "Yearly":
          this.rangePickerType = "year";
          break;

        default:
          this.rangePickerType = "date";
      }

      this.form.errors.clear();
    },

    isPast(time) {
      const timeMoment = moment(time);
      const today = moment().startOf("day");

      return timeMoment.isBefore(today);
    },

    shouldShow(appointment) {
      if (this.type === "all") {
        return true;
      }

      if (this.type === "past" && this.isPast(appointment.time)) {
        return true;
      }

      return this.type === "future" && !this.isPast(appointment.time);
    },

    clearEndErrors() {
      this.form.errors.clear("recurring.occurences");
      this.form.errors.clear("recurring.not_later_than");
    },

    initDefaultAppointmentDuration(serviceTypes) {
      this.form.duration = "1.0";

      let types;
      if (serviceTypes === undefined) {
        types = this.$store.getters["jobs/getJobById"](this.currentJobId).service_types;
      } else {
        types = serviceTypes;
      }

      if (types && types.length > 0) {
        let longest = 0;

        types.map((type) => {
          if (type.duration > longest) {
            longest = type.duration;
          }

          return type;
        });

        if (longest !== 0) {
          this.form.duration = longest.toString();
        }
      }
    },

    isValidEmail(email) {
      return window.isValidEmail(email);
    },

    isValidMobile(mobile) {
      return window.isValidMobile(mobile);
    },

    formDateChanged() {
      //

      this.$store.commit(
        "tech-availability/updateCurrentDate",
        this.formDate === null ? null : moment(this.formDate).format("YYYY-MM-DD")
      );
    },
  },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
.list-group {
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

.list-group-item {
  padding: 12px;
}

.list-group-item:last-child {
  border-bottom: 0;
}

.list-group .alert {
  padding: 4px;
  margin-bottom: 6px;
  line-height: 0;
}
</style>
